import React, { FC, useState } from 'react';
import { DropDownMenu } from '../common/DropDownMenu';
import { SiteSelector } from './SiteSelector';
import { useTranslation } from 'react-i18next';
import { useIsAdmin, useIsInPartnerContext } from '../utils';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';
import { SpocState } from '../../app-store/authentication/authenticationReducer';
import { useSpocs } from '../../api/spocs/useSpocs';
import { useMatch, useNavigate } from 'react-router-dom';

function filterSpocs(spocs: ReadonlyArray<SpocState>, searchTerm: string = '') {
    const lowerSearchTerm = searchTerm.toLowerCase();
    const filteredSpocs: SpocState[] = [];
    spocs.forEach(s => {
        if (s.id.toLowerCase().indexOf(lowerSearchTerm) >= 0 || s.name.toLowerCase().indexOf(lowerSearchTerm) >= 0) {
            filteredSpocs.push({ ...s });
        } else if (s.sites && filterSpocs(s.sites, lowerSearchTerm).length > 0) {
            filteredSpocs.push({
                ...s,
                sites: s.sites ? filterSpocs(s.sites, lowerSearchTerm) : []
            });
        }
    });

    return filteredSpocs;
}

export const SpocSelector: FC = () => {
    const { t } = useTranslation();
    const [openSpoc, setOpenSpoc] = useState<string>();
    const [searchTerm, setSearchTerm] = useState('');
    const [expanded, setExpanded] = useState(false);
    const { setAuthenticatedSpocById, spoc: currentSpoc } = useAuthentication();
    const { data: allSpocs = [] } = useSpocs(true);
    const isInPartnerContext = useIsInPartnerContext();
    const navigate = useNavigate();

    const selectionListMatch = useMatch('/orderlists/:id');
    const orderDetailMatch = useMatch('/orders/:id');
    const localContentProductMatch = useMatch('/localcontent/products/:id');
    const localContentEditProductMatch = useMatch('/localcontent/products/:id/edit');

    const isSpocSpecificRouteMatch =
        selectionListMatch ||
        orderDetailMatch ||
        (localContentProductMatch && localContentProductMatch.params.id !== 'new') ||
        localContentEditProductMatch;

    const changeExpanded = (exp: boolean) => {
        setExpanded(exp);
        if (!exp) {
            setSearchTerm('');
            setOpenSpoc(undefined);
        }
    };

    const isSinglePartner = allSpocs.length === 1 && allSpocs[0].id !== null;
    const admin = useIsAdmin();

    let isSingleSpoc = false;
    if (allSpocs.length === 1) {
        const spocEntry = allSpocs[0];
        // has no partner but sites OR
        // has partner but no sites =
        // only one single spoc entry to show
        isSingleSpoc =
            (spocEntry.id === null && spocEntry.sites.length === 1) ||
            (spocEntry.id !== null && spocEntry.sites.length === 0);
    }

    if (!admin && isSingleSpoc) {
        return (
            <div className="fl-right" style={{ marginLeft: '20px' }}>
                {currentSpoc.name}
            </div>
        );
    } else {
        let spocs = allSpocs;
        if (searchTerm) {
            spocs = filterSpocs(spocs, searchTerm);
        }

        // Reduce spocs for admins for performance
        if (admin && (!searchTerm || searchTerm.length < 2)) {
            spocs = spocs.filter(
                a => a.id === currentSpoc.id || a.sites.filter(b => b.id === currentSpoc.id).length > 0
            );
        }

        return (
            <DropDownMenu
                expanded={expanded}
                label={`${currentSpoc.name} (${
                    isInPartnerContext ? t('sharedCollection') : t('individualCollection')
                })`}
                className="spoc-selection just-dd expanded-dd dd-a-r fl-none"
                arrowClassName="md-uppest"
                align="right"
                onToggleClick={changeExpanded}
                onClickOutside={changeExpanded}
            >
                {admin ? (
                    <li data-close-dropdown="false">
                        <input
                            type="text"
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            placeholder={t('components.searchBar.shopSearch')}
                            data-testid="SpocSearch"
                        />
                    </li>
                ) : null}
                {spocs.map(spoc => (
                    <SiteSelector
                        key={spoc.id}
                        openSpoc={spoc => {
                            setOpenSpoc(spoc.id === openSpoc ? undefined : spoc.id);
                        }}
                        partner={spoc}
                        activeSpoc={currentSpoc}
                        isSinglePartner={!admin && isSinglePartner}
                        showDropdown={openSpoc === spoc.id}
                        changeSpoc={spoc => {
                            setAuthenticatedSpocById(spoc.id);
                            if (isSpocSpecificRouteMatch) {
                                navigate('/');
                            }
                            setExpanded(false);
                        }}
                        isAdmin={admin}
                    />
                ))}
            </DropDownMenu>
        );
    }
};
